import { useCallback, useEffect, useState } from 'react';
import { canvasService, type CanvasImage } from '@/services/canvasService';
import { useDebounce } from '../useDebounce';

const SEARCH_DEBOUNCE_MS = 500;

interface UseCanvasListParams {
  page: number;
  pageSize: number;
  search: string;
}

export const useCanvasList = ({ page, pageSize, search }: UseCanvasListParams) => {
  const debouncedSearch = useDebounce(search.trim(), SEARCH_DEBOUNCE_MS);
  const [canvases, setCanvases] = useState<CanvasImage[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    canvasService
      .getCanvases({ page, page_size: pageSize, search: debouncedSearch || undefined })
      .then((response) => {
        if (cancelled) return;
        setCanvases(response.results);
        setTotalCount(response.count);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setCanvases([]);
        setTotalCount(0);
        setError(err instanceof Error ? err.message : 'Error al obtener los canvas');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, pageSize, debouncedSearch, reloadKey]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  return { canvases, totalCount, isLoading, error, debouncedSearch, reload };
};
